import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';

// Types
import type { SocialProfileNetworkType } from '@/types/socialProfileType';

// Components
import H1 from '@/components/base/Headings/H1';
import { EmptyStateUpVote } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper/components/EmptyStateUpVote';
import { SupportedNetworkTypes } from '@/components/layouts/LayoutPlatform/components/PageContentWrapper/components/SupportedNetworkTypes';

export type PermanentEmptyStateProps = {
    title: string;
    description: string;
    supportedNetworkTypes?: SocialProfileNetworkType[];
    emptyStateName?: string;
    displayEmptyStateUpVote?: boolean;
};

export function PermanentEmptyState({
    title,
    description,
    supportedNetworkTypes,
    emptyStateName,
    displayEmptyStateUpVote = true
}: PermanentEmptyStateProps) {
    const { t } = useTranslation('layout_platform');
    return (
        <div className="h-full @container">
            <div className="flex h-full flex-col gap-15 rounded-xl bg-white p-10 @xl:p-20 @5xl:flex-row dark:bg-icoDarkMode-deepBlack">
                <div className="flex flex-col gap-10 @5xl:w-1/2">
                    <div>
                        <span className="rounded-3xl bg-icoGray-100 px-4 py-3 text-xs dark:bg-icoDarkMode-anthracite">
                            😶‍🌫️ {t('empty_states.not_yet_available')}
                        </span>
                    </div>
                    <div className="space-y-6">
                        <H1>{title}</H1>
                        <p className="font-GMedium text-xl text-icoGray-500 dark:text-icoDarkMode-wolf">
                            {description}
                        </p>
                    </div>
                    {supportedNetworkTypes ? (
                        <SupportedNetworkTypes supportedNetworkTypes={supportedNetworkTypes} />
                    ) : null}
                    {displayEmptyStateUpVote && emptyStateName ? (
                        <EmptyStateUpVote emptyStateName={emptyStateName} />
                    ) : null}
                </div>
                <div className="relative @5xl:w-1/2">
                    <div className="relative h-full w-full overflow-hidden rounded-3xl bg-icoEmptyStateIllustration p-10 @5xl:pr-0">
                        <div className="relative aspect-[875/494] h-full min-h-[500px] w-full @5xl:min-w-[800px]">
                            <Image
                                src={`/images/illustrations/empty-states/empty-state-${emptyStateName}.png`}
                                alt="illustration"
                                className="max-w-none rounded-2xl"
                                fill
                                objectFit="cover"
                                objectPosition="top left"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
